import { useState, useRef, useEffect } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaQuestion, setCaptchaQuestion] = useState({ num1: 0, num2: 0, answer: 0 });
  const captchaInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    generateCaptcha();
  }, []);

  const generateCaptcha = () => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptchaQuestion({ num1, num2, answer: num1 + num2 });
    setCaptchaValue('');
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (parseInt(captchaValue) !== captchaQuestion.answer) {
      setSubmitStatus('error');
      generateCaptcha();
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d69etvma728k8ctu2mug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        generateCaptcha();
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          <div>
            <div className="flex items-center mb-2">
              <img
                src="https://public.readdy.ai/ai/img_res/a2112a7d-cd2d-4121-9398-95a07520b04f.png"
                alt="KLCarPool"
                className="h-10 w-auto"
              />
            </div>
            <p className="text-sm font-bold text-white mb-1" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              KLCarPool
            </p>
            <p className="text-xs text-gray-400 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              by <strong className="font-bold">Drixem Group</strong>
            </p>
            <p className="text-sm text-gray-400 leading-relaxed mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
              Daily carpooling for same destination commuters. Smarter, safer, and more affordable transportation for Kuala Lumpur.
            </p>
            <div className="flex space-x-3">
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-lg w-5 h-5 flex items-center justify-center"></i>
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                <i className="ri-twitter-x-fill text-lg w-5 h-5 flex items-center justify-center"></i>
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                <i className="ri-linkedin-fill text-lg w-5 h-5 flex items-center justify-center"></i>
              </a>
              <a href="https://wa.me/60123835600" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-gray-800 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors cursor-pointer">
                <i className="ri-whatsapp-fill text-lg w-5 h-5 flex items-center justify-center"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold text-white uppercase mb-4 tracking-wider" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Quick Links
            </h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => scrollToSection('home')} className="text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Home
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('ai-features')} className="text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Features
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('verified-community')} className="text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Verified Community
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('investors')} className="text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Investors
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('waitlist')} className="text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Join Waitlist
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold text-white uppercase mb-4 tracking-wider" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Contact
            </h4>
            <ul className="space-y-2">
              <li>
                <a href="mailto:it@drixemgroup.com" className="text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}>
                  it@drixemgroup.com
                </a>
              </li>
              <li>
                <a href="https://wa.me/60123835600" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-teal-400 transition-colors cursor-pointer" style={{ fontFamily: "'Inter', sans-serif" }}>
                  +6012-3835600
                </a>
              </li>
              <li>
                <span className="text-sm text-gray-400" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Kuala Lumpur, Malaysia
                </span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold text-white uppercase mb-4 tracking-wider" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Stay Updated
            </h4>
            <form id="footer-newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="mb-4 space-y-3">
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                required
                className="w-full bg-gray-800 border border-gray-700 rounded-lg py-3 px-4 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-teal-500 transition-colors"
                style={{ fontFamily: "'Inter', sans-serif" }}
              />
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-400" style={{ fontFamily: "'Inter', sans-serif" }}>
                  {captchaQuestion.num1} + {captchaQuestion.num2} =
                </span>
                <input
                  ref={captchaInputRef}
                  type="text"
                  value={captchaValue}
                  onChange={(e) => setCaptchaValue(e.target.value)}
                  placeholder="?"
                  required
                  className="w-16 bg-gray-800 border border-gray-700 rounded-lg py-2 px-3 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-teal-500 transition-colors text-center"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-2 bg-teal-600 rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {isSubmitting ? '...' : 'Subscribe'}
                </button>
              </div>
            </form>
            {submitStatus === 'success' && (
              <p className="text-xs text-green-400" style={{ fontFamily: "'Inter', sans-serif" }}>
                Thanks for subscribing!
              </p>
            )}
            {submitStatus === 'error' && (
              <p className="text-xs text-red-400" style={{ fontFamily: "'Inter', sans-serif" }}>
                Please check your answer and try again.
              </p>
            )}
            <p className="text-xs text-gray-500 mt-2" style={{ fontFamily: "'Inter', sans-serif" }}>
              Get launch updates and exclusive early access.
            </p>
          </div>
        </div>

        <div className="border-t border-gray-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-center md:text-left">
              <p className="text-sm text-gray-400" style={{ fontFamily: "'Inter', sans-serif" }}>
                Developed by <strong className="font-bold">Drixem Group</strong> IT Division
              </p>
              <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                © 2025 All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
